import { useState, useEffect, useRef } from "react";

/* ─── DATA ──────────────────────────────────────────────────────── */
const pillars = [
  {
    id: "01",
    tag: "Research",
    title: "Quant Research",
    description:
      "Public research on quantitative strategies, signal design, factor models, and market microstructure. Built in the open so serious practitioners can stress-test, fork, and extend.",
    items: ["Factor model research", "Signal backtesting", "Alpha decay analysis", "Market microstructure"],
  },
  {
    id: "02",
    tag: "Infrastructure",
    title: "Crypto Execution Infra",
    description:
      "Low-latency execution engines, order routing systems, and multi-exchange connectivity built for real capital constraints — not for demos.",
    items: ["Multi-exchange connectors", "Order routing & slippage", "Position reconciliation", "Real-time risk checks"],
  },
  {
    id: "03",
    tag: "Strategies",
    title: "Experimental Strategies",
    description:
      "Live experimental trading strategies across crypto and equities. Some work, some don't — all are documented with methodology, results, and lessons.",
    items: ["Stat-arb experiments", "Momentum & mean-reversion", "Cross-asset signals", "Strategy attribution"],
  },
  {
    id: "04",
    tag: "Open Source",
    title: "Open-Source Tooling",
    description:
      "Libraries, frameworks, and utilities for quant practitioners — from backtesting harnesses to data pipelines. MIT licensed, maintained actively.",
    items: ["Backtesting framework", "Data ingestion pipelines", "Risk analytics library", "Strategy scaffolding CLI"],
  },
];

const stats = [
  { value: "<500ms", label: "Execution latency target", sub: "live systems" },
  { value: "100+", label: "Data points per backtest", sub: "per strategy run" },
  { value: "6+", label: "Years in production", sub: "live capital" },
  { value: "Open", label: "Research philosophy", sub: "built in public" },
];

const researchItems = [
  {
    type: "Strategy Note",
    title: "Momentum Decay in Crypto Micro-Caps",
    desc: "Signal half-life analysis across 200+ tokens. Findings suggest 4–8hr windows dominate alpha before decay.",
    date: "2025 Q2",
    status: "Published",
  },
  {
    type: "Infra Deep-Dive",
    title: "Sub-500ms Order Routing on Binance Futures",
    desc: "Architecture walkthrough of multi-account execution with slippage modeling and async reconciliation.",
    date: "2025 Q1",
    status: "Published",
  },
  {
    type: "Open Source",
    title: "backtest-core v0.4 — Multi-Asset Engine",
    desc: "Configurable backtesting core with realistic fill simulation, portfolio-level position tracking, and pluggable strategy layers.",
    date: "2024 Q4",
    status: "Released",
  },
  {
    type: "Experiment Log",
    title: "Stat-Arb on BTC/ETH Perps: 90-Day Debrief",
    desc: "Full debrief on a live stat-arb experiment — what the model got right, where spreads compressed, and how we adapted.",
    date: "2024 Q3",
    status: "Archived",
  },
];

const techStack = [
  "Python", "Rust", "TypeScript", "PostgreSQL", "Redis",
  "Kafka", "WebSockets", "REST/FIX", "TA-Lib", "NumPy",
  "Pandas", "FastAPI", "Docker", "AWS", "Vercel",
];

/* ─── HOOKS ─────────────────────────────────────────────────────── */
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, y = 28, className = "", style = {} }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? "translateY(0)" : `translateY(${y}px)`,
      transition: `opacity 0.75s ease ${delay}s, transform 0.75s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── TICKER ─────────────────────────────────────────────────────── */
function LiveTicker() {
  const [ticks, setTicks] = useState([
    { sym: "BTC/USDT", price: 67842.50, change: +1.24 },
    { sym: "ETH/USDT", price: 3521.80, change: -0.38 },
    { sym: "SOL/USDT", price: 182.44, change: +2.91 },
    { sym: "NIFTY50", price: 22847.00, change: +0.62 },
    { sym: "BNB/USDT", price: 612.30, change: -0.14 },
  ]);

  useEffect(() => {
    const id = setInterval(() => {
      setTicks(prev => prev.map(t => ({
        ...t,
        price: +(t.price * (1 + (Math.random() - 0.5) * 0.0006)).toFixed(2),
        change: +(t.change + (Math.random() - 0.5) * 0.05).toFixed(2),
      })));
    }, 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ display: "flex", gap: 36, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
      {ticks.map(t => (
        <div key={t.sym} style={{ flexShrink: 0, display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.08em" }}>{t.sym}</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--fg)", fontWeight: 500 }}>{t.price.toLocaleString()}</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: t.change >= 0 ? "var(--green)" : "var(--red)" }}>
            {t.change >= 0 ? "+" : ""}{t.change}%
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── ANIMATED COUNTER ───────────────────────────────────────────── */
function GridBackground() {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
        </pattern>
        <radialGradient id="fade" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor="transparent" />
          <stop offset="100%" stopColor="#050810" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <rect width="100%" height="100%" fill="url(#fade)" />
    </svg>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────────── */
export default function FavytLabsLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [activeResearch, setActiveResearch] = useState(0);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#050810",
      color: "#D4D4C8",
      fontFamily: "'Syne', 'Helvetica Neue', sans-serif",
      overflowX: "hidden",
      position: "relative",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@300;400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --green:   #00C8D4;
          --green-d: rgba(34, 197, 94, 0.12);
          --green-g: rgba(34, 197, 94, 0.06);
          --amber:   #F59E0B;
          --amber-d: rgba(245, 158, 11, 0.12);
          --red:     #EF4444;
          --fg:      #E8E8E2;
          --fg2:     #A1A19A;
          --dim:     #5A5A56;
          --border:  rgba(255,255,255,0.07);
          --border2: rgba(255,255,255,0.12);
          --surface: rgba(255,255,255,0.03);
          --mono:    'JetBrains Mono', 'Fira Code', monospace;
          --serif:   'Instrument Serif', Georgia, serif;
          --sans:    'Syne', 'Helvetica Neue', sans-serif;
        }

        html { scroll-behavior: smooth; }

        a { text-decoration: none; color: inherit; }

        ::selection { background: var(--green-d); color: var(--green); }

        /* scrollbar */
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

        .mono { font-family: var(--mono) !important; }
        .serif { font-family: var(--serif) !important; }

        /* NAV */
        .nav-link {
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--fg2);
          transition: color 0.2s;
          position: relative;
          padding-bottom: 2px;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 0; height: 1px;
          background: var(--green);
          transition: width 0.25s ease;
        }
        .nav-link:hover { color: var(--fg); }
        .nav-link:hover::after { width: 100%; }

        /* BUTTONS */
        .btn-g {
          display: inline-flex; align-items: center; gap: 8px;
          border: 1px solid var(--green);
          color: var(--green);
          background: var(--green-g);
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
          font-family: var(--sans);
        }
        .btn-g:hover {
          background: var(--green-d);
          box-shadow: 0 0 24px rgba(34,197,94,0.15);
          transform: translateY(-1px);
        }

        .btn-o {
          display: inline-flex; align-items: center; gap: 8px;
          border: 1px solid var(--border2);
          color: var(--fg2);
          background: var(--surface);
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s, transform 0.15s;
          font-family: var(--sans);
        }
        .btn-o:hover {
          border-color: var(--fg2);
          color: var(--fg);
          transform: translateY(-1px);
        }

        /* EYEBROW */
        .eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          font-family: var(--mono);
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--green);
        }
        .eyebrow::before {
          content: '';
          width: 20px; height: 1px;
          background: var(--green);
          display: block;
          flex-shrink: 0;
        }

        /* CARDS */
        .card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          transition: border-color 0.3s, background 0.3s, transform 0.3s;
          position: relative;
          overflow: hidden;
        }
        .card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--green), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .card:hover {
          border-color: rgba(34,197,94,0.2);
          background: rgba(255,255,255,0.045);
          transform: translateY(-3px);
        }
        .card:hover::before { opacity: 1; }

        /* TERMINAL BLOCK */
        .terminal {
          background: rgba(0,0,0,0.6);
          border: 1px solid var(--border2);
          border-radius: 10px;
          font-family: var(--mono);
          font-size: 12px;
          line-height: 1.9;
          overflow: hidden;
        }
        .terminal-bar {
          background: rgba(255,255,255,0.04);
          border-bottom: 1px solid var(--border);
          padding: 10px 16px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .dot { width: 10px; height: 10px; border-radius: 50%; }

        /* RESEARCH ITEM */
        .r-item {
          padding: 20px 0;
          border-top: 1px solid var(--border);
          cursor: pointer;
          transition: padding-left 0.2s;
        }
        .r-item:hover { padding-left: 8px; }
        .r-item.active { padding-left: 8px; border-top-color: var(--green); }

        /* TECH PILL */
        .tech-pill {
          font-family: var(--mono);
          font-size: 11px;
          letter-spacing: 0.06em;
          border: 1px solid var(--border);
          border-radius: 4px;
          padding: 5px 12px;
          color: var(--fg2);
          background: var(--surface);
          transition: border-color 0.2s, color 0.2s;
          white-space: nowrap;
        }
        .tech-pill:hover { border-color: var(--green); color: var(--green); }

        /* STATUS BADGE */
        .badge {
          font-family: var(--mono);
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 3px 10px;
          border-radius: 3px;
        }
        .badge-pub { background: var(--green-d); color: var(--green); border: 1px solid rgba(34,197,94,0.25); }
        .badge-rel { background: var(--amber-d); color: var(--amber); border: 1px solid rgba(245,158,11,0.25); }
        .badge-arc { background: rgba(255,255,255,0.04); color: var(--dim); border: 1px solid var(--border); }

        .divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border2), transparent);
          margin: 0;
        }

        /* AMBIENT BLOBS */
        .blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          pointer-events: none;
        }

        @media (max-width: 900px) {
          .hero-grid, .insights-grid, .contact-grid { grid-template-columns: 1fr !important; }
          .pillars-grid { grid-template-columns: 1fr 1fr !important; }
          .stats-bar { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 600px) {
          .pillars-grid, .stats-bar { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── NAVBAR ───────────────────────────────────── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        padding: "0 32px",
        background: scrolled ? "rgba(5,8,16,0.95)" : "transparent",
        borderBottom: `1px solid ${scrolled ? "var(--border)" : "transparent"}`,
        backdropFilter: scrolled ? "blur(20px)" : "none",
        transition: "background 0.3s, border-color 0.3s",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontFamily: "var(--sans)", fontSize: 20, fontWeight: 800, color: "var(--fg)", letterSpacing: "-0.03em" }}>
              favyt<span style={{ color: "var(--green)" }}>.</span>in
            </span>
            <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.1em" }}>LABS</span>
          </div>

          <nav style={{ display: "flex", gap: 32, alignItems: "center" }}>
            {[["Research", "#research"], ["Infra", "#infra"], ["Open Source", "#open-source"], ["Contact", "#contact"]].map(([label, href]) => (
              <a key={label} href={href} className="nav-link">{label}</a>
            ))}
            <a href="https://favyt.in" className="btn-g" style={{ padding: "8px 18px", fontSize: 11 }}>
              favyt.in ↗
            </a>
          </nav>
        </div>
      </header>

      {/* ── HERO ─────────────────────────────────────── */}
      <section style={{ position: "relative", overflow: "hidden", paddingTop: 48 }}>
        <GridBackground />
        <div className="blob" style={{ width: 500, height: 500, background: "rgba(34,197,94,0.07)", top: -100, left: "20%" }} />
        <div className="blob" style={{ width: 300, height: 300, background: "rgba(245,158,11,0.05)", top: 200, right: "10%" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px", position: "relative", zIndex: 2 }}>

          {/* live ticker strip */}
          <div style={{ borderBottom: "1px solid var(--border)", paddingBottom: 20, marginBottom: 64 }}>
            <LiveTicker />
          </div>

          <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 64, alignItems: "start" }}>
            <div>
              <Reveal>
                <div className="eyebrow" style={{ marginBottom: 24 }}>Quant Lab · Crypto Infra · Experimental</div>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 style={{
                  fontFamily: "var(--sans)",
                  fontWeight: 800,
                  fontSize: "clamp(3rem, 6vw, 5.5rem)",
                  lineHeight: 0.95,
                  letterSpacing: "-0.04em",
                  color: "var(--fg)",
                  marginBottom: 8,
                }}>
                  Favyt
                </h1>
                <h1 style={{
                  fontFamily: "var(--serif)",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: "clamp(3rem, 6vw, 5.5rem)",
                  lineHeight: 0.95,
                  letterSpacing: "-0.02em",
                  color: "var(--green)",
                  marginBottom: 36,
                }}>
                  Labs
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p style={{ fontSize: 17, lineHeight: 1.75, color: "var(--fg2)", maxWidth: 560, marginBottom: 40 }}>
                  Public quant research, crypto execution infrastructure, experimental trading strategies, and open-source tooling — <span style={{ color: "var(--fg)" }}>built in the open</span> at favyt.in.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <a href="https://favyt.in" className="btn-g">
                    Explore favyt.in
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </a>
                  <a href="#research" className="btn-o">View research</a>
                </div>
              </Reveal>
            </div>

            {/* terminal card */}
            <Reveal delay={0.15} y={20}>
              <div className="terminal">
                <div className="terminal-bar">
                  <div className="dot" style={{ background: "#EF4444" }} />
                  <div className="dot" style={{ background: "#F59E0B" }} />
                  <div className="dot" style={{ background: "#22C55E" }} />
                  <span style={{ marginLeft: 8, fontSize: 11, color: "var(--dim)", letterSpacing: "0.08em" }}>
                    favyt-labs / backtest-core
                  </span>
                </div>
                <div style={{ padding: "20px 20px 24px" }}>
                  {[
                    { prefix: "$", text: "git clone https://github.com/favyt-labs/backtest-core", color: "var(--fg2)" },
                    { prefix: "", text: "Cloning into 'backtest-core'...", color: "var(--dim)" },
                    { prefix: "$", text: "pip install favyt-backtest", color: "var(--fg2)" },
                    { prefix: "", text: "Successfully installed favyt-backtest-0.4.1", color: "var(--green)" },
                    { prefix: "$", text: "python run_strategy.py --symbol BTC/USDT \\", color: "var(--fg2)" },
                    { prefix: "", text: "  --start 2023-01-01 --capital 100000", color: "var(--fg2)" },
                    { prefix: "", text: "▶ Running backtest...", color: "var(--amber)" },
                    { prefix: "", text: "  Sharpe: 1.84  |  Max DD: -8.2%  |  Win: 61%", color: "var(--green)" },
                    { prefix: "", text: "  Total Return: +34.7%  |  Trades: 2,142", color: "var(--green)" },
                    { prefix: "", text: "✓ Backtest complete. Results → ./output/", color: "var(--fg2)" },
                  ].map((line, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.8 }}>
                      {line.prefix && <span style={{ color: "var(--green)", flexShrink: 0 }}>{line.prefix}</span>}
                      <span style={{ color: line.color }}>{line.text}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 10, fontFamily: "var(--mono)", fontSize: 12, marginTop: 4 }}>
                    <span style={{ color: "var(--green)" }}>$</span>
                    <span style={{ color: "var(--fg)", animation: "blink 1.2s step-end infinite" }}>▌</span>
                  </div>
                  <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* ── STATS BAR ── */}
        <div style={{ position: "relative", zIndex: 2, marginTop: 80, borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "rgba(0,0,0,0.3)" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
            <div className="stats-bar" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
              {stats.map((s, i) => (
                <div key={s.label} style={{
                  padding: "32px 0",
                  borderRight: i < 3 ? "1px solid var(--border)" : "none",
                  paddingLeft: i > 0 ? 40 : 0,
                  paddingRight: i < 3 ? 40 : 0,
                }}>
                  <Reveal delay={i * 0.08}>
                    <div style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", color: "var(--green)", lineHeight: 1, letterSpacing: "-0.04em", marginBottom: 8 }}>
                      {s.value}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--fg)", marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{s.sub}</div>
                  </Reveal>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT WE BUILD ────────────────────────────── */}
      <section id="infra" style={{ padding: "100px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 64, flexWrap: "wrap", gap: 24 }}>
              <div>
                <div className="eyebrow" style={{ marginBottom: 16 }}>What we build</div>
                <h2 style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", letterSpacing: "-0.04em", color: "var(--fg)", lineHeight: 1.1 }}>
                  Four pillars of<br />
                  <span style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontWeight: 400, color: "var(--green)" }}>serious systems work.</span>
                </h2>
              </div>
              <p style={{ maxWidth: 360, fontSize: 14, lineHeight: 1.75, color: "var(--fg2)" }}>
                Everything published, open-sourced, and documented — because serious practitioners deserve to see the methodology, not just the results.
              </p>
            </div>
          </Reveal>

          <div className="pillars-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {pillars.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <div className="card" style={{ padding: "28px 24px", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.1em" }}>{p.id}</span>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 10, padding: "3px 10px", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 3, color: "var(--green)", letterSpacing: "0.1em", textTransform: "uppercase" }}>{p.tag}</span>
                  </div>
                  <h3 style={{ fontFamily: "var(--sans)", fontWeight: 700, fontSize: 18, color: "var(--fg)", letterSpacing: "-0.02em", marginBottom: 14 }}>{p.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.7, color: "var(--fg2)", marginBottom: 24 }}>{p.description}</p>
                  <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
                    {p.items.map(item => (
                      <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--fg2)" }}>
                        <span style={{ color: "var(--green)", fontSize: 10, flexShrink: 0 }}>◆</span>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}><div className="divider" /></div>

      {/* ── RESEARCH LOG ──────────────────────────────── */}
      <section id="research" style={{ padding: "100px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="insights-grid" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 80, alignItems: "start" }}>
            <Reveal>
              <div style={{ position: "sticky", top: 100 }}>
                <div className="eyebrow" style={{ marginBottom: 20 }}>Research Log</div>
                <h2 style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: "clamp(1.8rem, 3vw, 2.6rem)", letterSpacing: "-0.04em", color: "var(--fg)", lineHeight: 1.1, marginBottom: 20 }}>
                  Built in the open.<br />
                  <span style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontWeight: 400, color: "var(--fg2)" }}>Published with receipts.</span>
                </h2>
                <p style={{ fontSize: 14, lineHeight: 1.8, color: "var(--fg2)", marginBottom: 36 }}>
                  Every strategy note, infra deep-dive, and experiment log includes methodology, data, and honest results — including the failures.
                </p>
                <a href="https://favyt.in" className="btn-g" style={{ display: "inline-flex" }}>
                  All research ↗
                </a>

                {/* selected detail card */}
                <div className="terminal" style={{ marginTop: 40 }}>
                  <div className="terminal-bar">
                    <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.1em" }}>selected / {researchItems[activeResearch].type.toLowerCase().replace(/ /g, "-")}</span>
                  </div>
                  <div style={{ padding: "16px 20px", fontFamily: "var(--mono)", fontSize: 12 }}>
                    <div style={{ color: "var(--green)", marginBottom: 6 }}>// {researchItems[activeResearch].type}</div>
                    <div style={{ color: "var(--fg)", fontWeight: 500, marginBottom: 8, lineHeight: 1.5 }}>{researchItems[activeResearch].title}</div>
                    <div style={{ color: "var(--fg2)", lineHeight: 1.7, fontSize: 11 }}>{researchItems[activeResearch].desc}</div>
                  </div>
                </div>
              </div>
            </Reveal>

            <div>
              {researchItems.map((item, i) => (
                <Reveal key={item.title} delay={i * 0.07}>
                  <div
                    className={`r-item ${activeResearch === i ? "active" : ""}`}
                    onClick={() => setActiveResearch(i)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.08em" }}>{item.type}</span>
                        <span className={`badge ${item.status === "Published" ? "badge-pub" : item.status === "Released" ? "badge-rel" : "badge-arc"}`}>
                          {item.status}
                        </span>
                      </div>
                      <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", flexShrink: 0 }}>{item.date}</span>
                    </div>
                    <h3 style={{ fontFamily: "var(--sans)", fontWeight: 700, fontSize: 17, color: activeResearch === i ? "var(--green)" : "var(--fg)", letterSpacing: "-0.02em", marginBottom: 8, lineHeight: 1.3, transition: "color 0.2s" }}>
                      {item.title}
                    </h3>
                    <p style={{ fontSize: 13, lineHeight: 1.7, color: "var(--fg2)" }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}><div className="divider" /></div>

      {/* ── OPEN SOURCE ───────────────────────────────── */}
      <section id="open-source" style={{ padding: "100px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
            <Reveal>
              <div>
                <div className="eyebrow" style={{ marginBottom: 20 }}>Open Source</div>
                <h2 style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: "clamp(1.8rem, 3vw, 2.6rem)", letterSpacing: "-0.04em", color: "var(--fg)", lineHeight: 1.1, marginBottom: 20 }}>
                  Tooling for<br />
                  <span style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontWeight: 400, color: "var(--amber)" }}>serious practitioners.</span>
                </h2>
                <p style={{ fontSize: 14, lineHeight: 1.8, color: "var(--fg2)", marginBottom: 32 }}>
                  MIT-licensed libraries built from real trading operations — not tutorial projects. Actively maintained, documented, and pressure-tested with live capital.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 40 }}>
                  {[
                    { name: "backtest-core", desc: "Multi-asset backtesting with realistic fill simulation", stars: "MIT · Python" },
                    { name: "crypto-exec", desc: "Low-latency exchange connectors & order routing", stars: "MIT · Python / Rust" },
                    { name: "risk-toolkit", desc: "Portfolio risk analytics and drawdown monitoring", stars: "MIT · Python" },
                  ].map(repo => (
                    <div key={repo.name} style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)", borderRadius: 8, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "border-color 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(34,197,94,0.3)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                    >
                      <div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--green)", fontWeight: 500, marginBottom: 4 }}>
                          favyt-labs / {repo.name}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--fg2)" }}>{repo.desc}</div>
                      </div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.06em", textAlign: "right", flexShrink: 0, marginLeft: 16 }}>{repo.stars}</div>
                    </div>
                  ))}
                </div>

                <a href="https://github.com/favyt-labs" className="btn-o" style={{ display: "inline-flex" }}>
                  GitHub ↗
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 20 }}>
                  // tech stack
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {techStack.map((t, i) => (
                    <span key={t} className="tech-pill" style={{ animationDelay: `${i * 0.04}s` }}>{t}</span>
                  ))}
                </div>

                <div style={{ marginTop: 48, padding: "28px", background: "rgba(0,0,0,0.5)", border: "1px solid var(--border)", borderRadius: 12 }}>
                  <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--green)", letterSpacing: "0.1em", marginBottom: 16 }}>// philosophy</div>
                  <blockquote style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 19, color: "var(--fg)", lineHeight: 1.6, borderLeft: "2px solid var(--green)", paddingLeft: 20 }}>
                    "We share what we build because hidden methodology helps nobody. If the strategy is robust, sunlight makes it stronger."
                  </blockquote>
                  <div style={{ marginTop: 16, fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.08em" }}>
                    — Favyt Labs Research Philosophy
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}><div className="divider" /></div>

      {/* ── WHO THIS IS FOR ───────────────────────────── */}
      <section style={{ padding: "100px 32px", position: "relative", overflow: "hidden" }}>
        <div className="blob" style={{ width: 400, height: 400, background: "rgba(34,197,94,0.05)", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }} />
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <div className="eyebrow" style={{ justifyContent: "center", marginBottom: 20 }}>Who this is for</div>
              <h2 style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", letterSpacing: "-0.04em", color: "var(--fg)", lineHeight: 1.1 }}>
                Built for people who take<br />
                <span style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontWeight: 400, color: "var(--green)" }}>trading seriously.</span>
              </h2>
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {[
              {
                id: "01",
                title: "Quant Practitioners",
                desc: "Individuals and small teams running systematic strategies who want peer-reviewed methodology, not pitch decks.",
              },
              {
                id: "02",
                title: "Prop Desks & Crypto Teams",
                desc: "Operations that need proven execution infrastructure, risk tooling, or research inputs without building from scratch.",
              },
              {
                id: "03",
                title: "Open-Source Contributors",
                desc: "Engineers who want to extend backtesting frameworks, improve execution connectors, or contribute to shared tooling.",
              },
            ].map((item, i) => (
              <Reveal key={item.id} delay={i * 0.1}>
                <div className="card" style={{ padding: "32px 28px" }}>
                  <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.1em", marginBottom: 20 }}>{item.id}</div>
                  <h3 style={{ fontFamily: "var(--sans)", fontWeight: 700, fontSize: 20, color: "var(--fg)", letterSpacing: "-0.02em", marginBottom: 14 }}>{item.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--fg2)" }}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT / CTA ─────────────────────────────── */}
      <section id="contact" style={{ padding: "100px 32px", background: "rgba(0,0,0,0.4)", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
            <Reveal>
              <div>
                <div className="eyebrow" style={{ marginBottom: 20 }}>Get involved</div>
                <h2 style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: "clamp(2rem, 4vw, 3.2rem)", letterSpacing: "-0.04em", color: "var(--fg)", lineHeight: 1.05, marginBottom: 24 }}>
                  Follow the work.<br />
                  <span style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontWeight: 400, color: "var(--green)" }}>Build with us.</span>
                </h2>
                <p style={{ fontSize: 15, lineHeight: 1.8, color: "var(--fg2)", maxWidth: 440, marginBottom: 40 }}>
                  Whether you're a quant practitioner, crypto engineer, or just deeply curious about systematic trading — Favyt Labs publishes openly and collaborates selectively.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {[
                    { label: "Research updates", value: "favyt.in/subscribe" },
                    { label: "Collaborate / consult", value: "labs@favyt.in" },
                    { label: "Open source", value: "github.com/favyt-labs" },
                    { label: "Consulting arm", value: "favyt.com" },
                  ].map((row, i) => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{row.label}</span>
                      <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--green)" }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div>
                <div className="terminal" style={{ marginBottom: 20 }}>
                  <div className="terminal-bar">
                    <div className="dot" style={{ background: "#EF4444" }} />
                    <div className="dot" style={{ background: "#F59E0B" }} />
                    <div className="dot" style={{ background: "#22C55E" }} />
                    <span style={{ marginLeft: 8, fontSize: 11, color: "var(--dim)", letterSpacing: "0.08em" }}>reach out</span>
                  </div>
                  <div style={{ padding: "20px" }}>
                    <input placeholder="Name" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 6, padding: "12px 14px", fontFamily: "var(--mono)", fontSize: 12, color: "var(--fg)", marginBottom: 12, outline: "none" }}
                      onFocus={e => e.target.style.borderColor = "var(--green)"}
                      onBlur={e => e.target.style.borderColor = "var(--border)"}
                    />
                    <input placeholder="Email / GitHub" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 6, padding: "12px 14px", fontFamily: "var(--mono)", fontSize: 12, color: "var(--fg)", marginBottom: 12, outline: "none" }}
                      onFocus={e => e.target.style.borderColor = "var(--green)"}
                      onBlur={e => e.target.style.borderColor = "var(--border)"}
                    />
                    <textarea placeholder="What are you building or researching?" rows={4} style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 6, padding: "12px 14px", fontFamily: "var(--mono)", fontSize: 12, color: "var(--fg)", marginBottom: 16, outline: "none", resize: "vertical" }}
                      onFocus={e => e.target.style.borderColor = "var(--green)"}
                      onBlur={e => e.target.style.borderColor = "var(--border)"}
                    />
                    <button className="btn-g" style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: 12, borderRadius: 6 }}>
                      Send message
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </button>
                  </div>
                </div>
                <p style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.08em", textAlign: "center" }}>
                  // We read everything. We respond selectively.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────── */}
      <footer style={{ borderTop: "1px solid var(--border)", padding: "28px 32px", background: "rgba(0,0,0,0.5)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontFamily: "var(--sans)", fontSize: 18, fontWeight: 800, color: "var(--fg)", letterSpacing: "-0.03em" }}>
              favyt<span style={{ color: "var(--green)" }}>.</span>in
            </span>
            <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)" }}>LABS · QUANT · CRYPTO · OSS</span>
          </div>
          <div style={{ display: "flex", gap: 28 }}>
            {[["Research", "#research"], ["Infra", "#infra"], ["Open Source", "#open-source"], ["favyt.com", "https://favyt.com"]].map(([label, href]) => (
              <a key={label} href={href} style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--dim)", letterSpacing: "0.08em", textTransform: "uppercase", transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = "var(--green)"}
                onMouseLeave={e => e.target.style.color = "var(--dim)"}
              >
                {label}
              </a>
            ))}
          </div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--dim)", letterSpacing: "0.06em" }}>
            © 2026 Favyt Technologies LLP
          </div>
        </div>
      </footer>
    </div>
  );
}
